import { PERMISSIONS, terraContract } from '@terra/shared-authorization';
import {
  MAX_REMEDIATION_BRANCH_NAME_LENGTH,
  MAX_REMEDIATION_PR_TITLE_LENGTH,
} from '@terra/shared-utils';
import { FINDINGS_PATH_NAME } from './findings.constants';
import {
  FindingsListSelectionSchema,
  ResolveFindingsListSelectionResponseSchema,
  UpdateFindingsByListSelectionSchema,
} from './findings-list-selection.schema';
import {
  CountFindingsGroupsByFilterBodySchema,
  CountFindingsGroupsByFilterResponseSchema,
} from './findings-group-counts.schema';
import {
  FindingStatsSchema,
  FindingsMetricSchema,
  FindingStatsQuerySchema,
  FindingFilterQuerySchema,
  FindingRiskScoreSchema,
  FindingOverTimeSchema,
  FindingStatusSchemaResponse,
  FindingStatusDistributionSchema,
  FindingListDtoSchema,
  FindingDetailDtoSchema,
  FindingDetailEnrichedDrilldownDtoSchema,
  CustomerFindingUpdateSchema,
  FindingUpdateInputSchema,
  BulkDeleteFindingsPayloadSchema,
  BulkDeleteFindingsResponseSchema,
  BulkUpdateFindingsPayloadSchema,
  BulkUpdateFindingsResponseSchema,
  BulkDeleteFindingsByFilterPayloadSchema,
  BulkUpdateFindingsByFilterPayloadSchema,
  BulkFilterTooManyMatchesResponseSchema,
  UnpublishedConstituentsErrorSchema,
  FindingTimelineDtoSchema,
  FindingsByAgeBucketsSchema,
  FindingsByApplicationDetailRequestBodySchema,
  FindingsByApplicationListRequestBodySchema,
  FindingsListQuerySchema,
  FindingsListResponseSchema,
  FindingLinkedAssetCountsQuerySchema,
  FindingLinkedAssetCountsResponseSchema,
  GeneratePocScriptPathParamsSchema,
  GeneratePocScriptAcceptedSchema,
  GeneratePocScriptErrorSchema,
  ReplacePocScriptBodySchema,
  ReplaceLinkedTicketBodySchema,
  FindingPocImageUploadBodySchema,
  FindingPocImageDeleteResponseSchema,
  FindingPocImagePathParamsSchema,
  FindingPocImageSignedUrlResponseSchema,
  FindingPocImageUploadPathParamsSchema,
  FindingPocImageUploadResponseSchema,
} from './findings.schemas';
import { FindingCreateBodyDtoSchema } from './finding-create.schema';
import {
  CreateExternalNetworkFindingChainBodySchema,
  CreateExternalNetworkFindingChainResponseSchema,
} from './finding-chain-from-selection.schema';
import { getFindingAttackPathContract } from './finding-attack-path.contract';
import {
  FindingAuditLogsResponseSchema,
  MTTRDataSchema,
  AggregateMTTRDataSchema,
  AggregateMTTRQuerySchema,
  FindingsOverTimeResponseSchema,
  FindingsOverTimeQuerySchema,
  FindingsByStatusOverTimeResponseSchema,
  FindingsByStatusOverTimeQuerySchema,
  DashboardTrendQuerySchema,
  MTTROverTimeResponseSchema,
  ResolvedUnresolvedTrendResponseSchema,
} from './audit.schemas';
import {
  DecideSeverityChangeRequestBodySchema,
  SubmitSeverityChangeRequestBodySchema,
} from './severity-change-request.schemas';
import { PaginationQueryParamsSchema } from '@terra/shared-dto-types';
import { z } from 'zod';

export const FindingsContract = terraContract
  .prefix(`/${FINDINGS_PATH_NAME}`)
  .router({
    getAllFindings: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/',
        summary: 'List all findings',
        description:
          'Retrieves all security findings across all applications for the authenticated tenant. Returns minimal data suitable for list views. Use pagination parameters to handle large result sets.',
        inputStructure: 'detailed',
      })
      .input(z.object({ query: PaginationQueryParamsSchema }))
      .output(FindingListDtoSchema.array()),
    getFindingsByApplicationId: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'POST',
        path: '/application/{id}',
        summary: 'Get findings by application',
        description:
          'Retrieves findings for a specific application with minimal data suitable for list views. Use /findings/id/:id for full finding details.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ id: z.string() }),
          query: PaginationQueryParamsSchema,
          body: FindingsByApplicationDetailRequestBodySchema,
        }),
      )
      .output(FindingListDtoSchema.array()),
    getAllFindingsByApplicationId: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'POST',
        path: '/application/{id}/all',
        summary: 'Get all findings by application (list view)',
        description:
          'Retrieves all findings for a specific application with minimal data suitable for list views.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ id: z.string() }),
          query: PaginationQueryParamsSchema,
          body: FindingsByApplicationListRequestBodySchema,
        }),
      )
      .output(FindingListDtoSchema.array()),
    getFinding: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/id/{id}',
        summary: 'Get finding details',
        description:
          'Retrieves complete details for a single finding including sensitive content like proof-of-concept, business impact, and remediation plan. Use this endpoint when viewing or editing a specific finding.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ id: z.string() }),
          query: z.object({
            includeUnpublished: z.coerce
              .boolean()
              .optional()
              .describe(
                'Include unpublished findings (requires Execute Tests permission)',
              ),
            targetId: z.coerce
              .string()
              .optional()
              .describe(
                'Restrict finding lookup to a specific target/application ID',
              ),
          }),
        }),
      )
      .output(FindingDetailDtoSchema),
    getAggregatedStats: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/stats/aggregated',
        summary: 'Get aggregated finding statistics',
        description:
          'Returns tenant findings metrics (total plus optional severity and status facets) across all applications.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          query: FindingStatsQuerySchema.optional(),
        }),
      )
      .output(FindingsMetricSchema),
    getStatsByApplicationId: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/stats/application/{id}',
        summary: 'Get finding statistics by application',
        description: 'Returns finding statistics for a specific application.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ id: z.string() }),
          query: FindingStatsQuerySchema,
        }),
      )
      .output(FindingStatsSchema),
    getFilteredFindings: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/filtered',
        summary: 'Get filtered findings',
        description:
          'Retrieves findings matching the specified filter criteria with minimal data for list views.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          query: FindingFilterQuerySchema.and(PaginationQueryParamsSchema),
        }),
      )
      .output(FindingListDtoSchema.array()),
    getAggregatedStatus: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/stats/aggregated/status',
        summary: 'Get aggregated status counts',
        description: 'Returns count of findings grouped by status.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          query: FindingStatsQuerySchema.optional(),
        }),
      )
      .output(FindingStatusSchemaResponse.array()),
    getAggregatedRiskScore: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/risk-score/aggregated',
        summary: 'Get aggregated risk score',
        description:
          'Returns the overall risk score calculated from all findings.',
        inputStructure: 'detailed',
      })
      .output(FindingRiskScoreSchema),
    getRiskScoreByApplicationId: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/risk-score/application/{id}',
        summary: 'Get risk score by application',
        description: 'Returns the risk score for a specific application.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ id: z.string() }) }))
      .output(FindingRiskScoreSchema),
    getAggregatedFindingsOverTime: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/time-series/aggregated',
        summary: 'Get findings trend over time',
        description:
          'Returns time series data showing finding counts over time for trend analysis.',
        inputStructure: 'detailed',
      })
      .output(FindingOverTimeSchema),
    getFindingsOverTimeByApplicationId: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/time-series/application/{id}',
        summary: 'Get findings trend by application',
        description:
          'Returns time series data for findings in a specific application.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ id: z.string() }) }))
      .output(FindingOverTimeSchema),
    getStatusDistribution: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/metrics/status-distribution',
        summary: 'Get status distribution',
        description:
          'Returns finding counts grouped by status for dashboard visualization.',
        inputStructure: 'detailed',
      })
      .output(FindingStatusDistributionSchema),
    getStatusDistributionByApplicationId: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/metrics/status-distribution/application/{id}',
        summary: 'Get status distribution by application',
        description:
          'Returns finding status distribution for a specific application.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ id: z.string() }) }))
      .output(FindingStatusDistributionSchema),
    getFindingsByAgeBuckets: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/metrics/by-age-buckets',
        summary: 'Get findings by age buckets',
        description:
          'Returns findings distributed by age buckets (0-7 days, 8-14 days, 15-30 days, 30+ days) grouped by severity with current and previous period values. Age is calculated from publishedAt.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          query: z.object({
            from: z
              .string()
              .optional()
              .describe('Start date for the metrics period (ISO string)'),
            to: z
              .string()
              .optional()
              .describe('End date for the metrics period (ISO string)'),
            environment: z
              .string()
              .optional()
              .describe('Filter by environment'),
            businessUnitId: z
              .string()
              .optional()
              .describe('Filter by business unit ID'),
            applicationId: z
              .string()
              .optional()
              .describe('Filter by application ID'),
            targetId: z
              .string()
              .optional()
              .describe('Alias for application ID'),
          }),
        }),
      )
      .output(FindingsByAgeBucketsSchema),
    updateFindingStatus: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'PATCH',
        path: '/{id}/status',
        summary: 'Update finding status',
        description:
          'Updates the status of a finding. Use this to mark findings as resolved, accepted risk, or false positive.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ id: z.string() }),
          body: CustomerFindingUpdateSchema,
        }),
      )
      .output(FindingDetailDtoSchema),
    listFindingsEnriched: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/list',
        summary: 'List findings with asset details (paged)',
        description:
          'Returns one page of findings with embedded asset labels and the total number of rows matching the filters. Scope with targetType and targetId (or targetScope).',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          query: FindingsListQuerySchema,
        }),
      )
      .output(FindingsListResponseSchema),
    getFindingLinkedAssetCounts: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/linked-asset-counts',
        summary: 'Count findings per linked asset id for a target',
        description:
          'Aggregates finding counts grouped by linked asset id for one application or AI application target. Omit includeUnpublished (or set false) for published-only counts; set true to include unpublished when the caller has Execute Tests permission.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          query: FindingLinkedAssetCountsQuerySchema,
        }),
      )
      .output(FindingLinkedAssetCountsResponseSchema),
    getFindingEnriched: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/id/{id}/enriched',
        summary: 'Get finding with asset details',
        description:
          'Retrieves a single finding with embedded endpoint/webpage asset information.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ id: z.string() }),
          query: z.object({
            includeUnpublished: z.coerce
              .boolean()
              .optional()
              .describe(
                'Include unpublished findings (requires Execute Tests permission)',
              ),
          }),
        }),
      )
      .output(FindingDetailEnrichedDrilldownDtoSchema),
    getFindingAttackPath: getFindingAttackPathContract,
    getFindingTimeline: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/id/{id}/timeline',
        summary: 'Get finding timeline',
        description:
          'Returns the history of status changes and events for a finding.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ id: z.string() }) }))
      .output(FindingTimelineDtoSchema),
    triggerRemediationGeneration: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/remediation',
        summary: 'Trigger remediation plan generation for a finding',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ id: z.string() }) }))
      .output(z.object({ message: z.string() })),
    createRemediationPr: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/remediation/pr',
        summary:
          'Trigger async PR/MR creation with remediation fixes for a finding',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ id: z.string() }),
          body: z
            .object({
              branchName: z
                .string()
                .trim()
                .min(1)
                .max(MAX_REMEDIATION_BRANCH_NAME_LENGTH)
                .optional(),
              prTitle: z
                .string()
                .trim()
                .min(1)
                .max(MAX_REMEDIATION_PR_TITLE_LENGTH)
                .optional(),
            })
            .optional(),
        }),
      )
      .output(
        z.object({
          triggered: z.boolean(),
          message: z.string(),
        }),
      )
      .errors({
        BAD_REQUEST: {
          status: 400,
          data: z.object({
            message: z.string(),
          }),
        },
        NOT_FOUND: {
          status: 404,
          data: z.object({
            message: z.string(),
          }),
        },
      }),
    getRemediationStatus: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/remediation/status',
        summary:
          'Check if remediation PR creation is in progress for a finding',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ id: z.string() }) }))
      .output(
        z.object({
          inProgress: z.boolean(),
          triggeredAt: z.iso.datetime().nullable(),
        }),
      ),
    verifyRemediationPrPermissions: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/remediation/pr/permissions',
        summary:
          'Verify git integration has permissions to create remediation PR',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ id: z.string() }) }))
      .output(
        z.object({
          hasPermission: z.boolean(),
          message: z.string().optional(),
        }),
      ),
    createFinding: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'POST',
        path: '/',
        summary: 'Create finding',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: FindingCreateBodyDtoSchema }))
      .output(FindingDetailDtoSchema),
    createExternalNetworkFindingChain: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'POST',
        path: '/chain/external-network',
        summary:
          'Create a published vulnerability-chaining signal from selected external-network findings',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          body: CreateExternalNetworkFindingChainBodySchema,
        }),
      )
      .output(CreateExternalNetworkFindingChainResponseSchema),
    updateFinding: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'PATCH',
        path: '/{id}',
        summary: 'Update finding',
        description: 'Updates user-editable fields of a finding.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ id: z.string() }),
          body: FindingUpdateInputSchema,
        }),
      )
      .output(FindingDetailDtoSchema)
      .errors({
        CONFLICT: { status: 409, data: UnpublishedConstituentsErrorSchema },
      }),
    uploadFindingPocImage: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/poc-images',
        summary: 'Upload a finding PoC image',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: FindingPocImageUploadPathParamsSchema,
          body: FindingPocImageUploadBodySchema,
        }),
      )
      .output(FindingPocImageUploadResponseSchema),
    getFindingPocImageSignedUrl: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/poc-images/{imageId}',
        summary: 'Get signed URL for finding PoC image',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: FindingPocImagePathParamsSchema,
        }),
      )
      .output(FindingPocImageSignedUrlResponseSchema),
    deleteFindingPocImage: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'DELETE',
        path: '/{id}/poc-images/{imageId}',
        summary: 'Delete a finding PoC image',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: FindingPocImagePathParamsSchema,
        }),
      )
      .output(FindingPocImageDeleteResponseSchema),
    requestFindingRetest: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/retest',
        summary: 'Request finding retest',
        description:
          'Requests automated verification for a finding, even if it is already in Retest status.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ id: z.string() }),
          body: z.object({}).optional(),
        }),
      )
      .output(FindingDetailDtoSchema),
    abortFindingRetest: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/retest/abort',
        summary: 'Abort in-flight finding retest',
        description:
          'Stops an in-flight retest immediately. Progress is discarded and the finding status is unchanged.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ id: z.string() }),
          body: z
            .object({
              threadId: z.string().optional(),
              executionId: z.string().optional(),
            })
            .optional(),
        }),
      )
      .output(FindingDetailDtoSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    createSeverityChangeRequest: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/severity-change-requests',
        summary: 'Request finding severity change',
        description:
          'Submits a request to change the severity of a finding, with a justification for review.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ id: z.string() }),
          body: SubmitSeverityChangeRequestBodySchema,
        }),
      )
      .output(FindingDetailDtoSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        FORBIDDEN: { status: 403, data: z.object({ message: z.string() }) },
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
        CONFLICT: { status: 409, data: z.object({ message: z.string() }) },
      }),
    decideSeverityChangeRequest: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'POST',
        path: '/{id}/severity-change-requests/{requestId}/decide',
        summary: 'Decide finding severity change request',
        description:
          'Approves or rejects a pending severity change request. Approval applies the proposed severity; rejection leaves severity unchanged.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ id: z.string(), requestId: z.string() }),
          body: DecideSeverityChangeRequestBodySchema,
        }),
      )
      .output(FindingDetailDtoSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        FORBIDDEN: { status: 403, data: z.object({ message: z.string() }) },
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
        CONFLICT: { status: 409, data: z.object({ message: z.string() }) },
      }),
    replacePocScript: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'PUT',
        path: '/{id}/poc-script',
        summary: 'Replace POC script',
        description:
          'Replaces the POC script for a finding. Content is encrypted at rest. Clears guardrails metadata since this is a user-uploaded script.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ id: z.string() }),
          body: ReplacePocScriptBodySchema,
        }),
      )
      .output(FindingDetailDtoSchema),
    replaceLinkedTicket: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'PUT',
        path: '/{id}/linked-ticket',
        summary: 'Replace linked provider ticket',
        description:
          'Replaces the existing linked provider ticket for a finding and reconciles any workflow side effects.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ id: z.string() }),
          body: ReplaceLinkedTicketBodySchema,
        }),
      )
      .output(FindingDetailDtoSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    updateFindingsBulk: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'PATCH',
        path: '/bulk',
        summary: 'Update multiple findings',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: BulkUpdateFindingsPayloadSchema }))
      .output(BulkUpdateFindingsResponseSchema)
      .errors({
        CONFLICT: { status: 409, data: UnpublishedConstituentsErrorSchema },
      }),
    deleteFindingsBulk: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'DELETE',
        path: '/bulk-delete',
        summary: 'Delete multiple findings',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: BulkDeleteFindingsPayloadSchema }))
      .output(BulkDeleteFindingsResponseSchema),
    countFindingsByFilter: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'POST',
        path: '/count-by-filter',
        summary: 'Count findings matching a filter',
        description:
          'Returns the count of findings matching the same filter shape used by the list endpoint. Used to power select-all UX without mutating data.',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: BulkDeleteFindingsByFilterPayloadSchema }))
      .output(z.object({ count: z.number() })),
    countFindingsGroupsByFilter: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'POST',
        path: '/count-groups-by-filter',
        summary: 'Count findings per group for a filter',
        description:
          'Returns one count per group value for findings matching the same filter as count-by-filter. Calendar-day groups use the optional IANA timeZone (UTC by default).',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: CountFindingsGroupsByFilterBodySchema }))
      .output(CountFindingsGroupsByFilterResponseSchema),
    deleteFindingsBulkByFilter: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'POST',
        path: '/bulk-delete-by-filter',
        summary: 'Delete every finding matching a filter',
        description:
          'Resolves the same filter the list endpoint uses, optionally subtracts excludedIds, and bulk-deletes the result. Rejects with 409 when the resolved set exceeds the server-side hard limit.',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: BulkDeleteFindingsByFilterPayloadSchema }))
      .output(BulkDeleteFindingsResponseSchema)
      .errors({
        CONFLICT: { status: 409, data: BulkFilterTooManyMatchesResponseSchema },
      }),
    resolveFindingsListSelection: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'POST',
        path: '/resolve-list-selection',
        summary: 'Resolve a findings list selection to rows',
        description:
          'Resolves a findings-table selection. allSelected uses the list filters. selectedIds uses those ids and ignores filters. excludedIds uses the list filters and omits those ids.',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: FindingsListSelectionSchema }))
      .output(ResolveFindingsListSelectionResponseSchema)
      .errors({
        CONFLICT: { status: 409, data: BulkFilterTooManyMatchesResponseSchema },
      }),
    updateFindingsBulkByListSelection: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'PATCH',
        path: '/bulk-by-list-selection',
        summary: 'Update findings matching a list selection',
        description:
          'Bulk-updates a findings-table selection. allSelected uses the list filters. selectedIds uses those ids and ignores filters. excludedIds uses the list filters and omits those ids.',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: UpdateFindingsByListSelectionSchema }))
      .output(BulkUpdateFindingsResponseSchema)
      .errors({
        CONFLICT: { status: 409, data: BulkFilterTooManyMatchesResponseSchema },
      }),
    updateFindingsBulkByFilter: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'PATCH',
        path: '/bulk-by-filter',
        summary: 'Update every finding matching a filter',
        description:
          'Resolves the same filter the list endpoint uses, optionally subtracts excludedIds, and bulk-updates the result. Rejects with 409 when the resolved set exceeds the server-side hard limit.',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: BulkUpdateFindingsByFilterPayloadSchema }))
      .output(BulkUpdateFindingsResponseSchema)
      .errors({
        CONFLICT: { status: 409, data: BulkFilterTooManyMatchesResponseSchema },
      }),
    deleteFinding: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_FINDINGS.name],
      })
      .route({
        method: 'DELETE',
        path: '/{id}',
        summary: 'Delete a single finding',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ id: z.string() }) }))
      .output(
        z.object({
          success: z.boolean(),
          message: z.string(),
        }),
      ),
    getAuditLogs: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/audit-logs',
        summary: 'Get audit logs for a finding',
        description:
          'Retrieves the complete audit trail for a finding, showing all changes made to the finding over time.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ id: z.string() }),
          query: PaginationQueryParamsSchema,
        }),
      )
      .output(FindingAuditLogsResponseSchema),
    getMTTR: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/mttr',
        summary: 'Get MTTR data for a finding',
        description:
          'Calculates Mean Time To Resolution (MTTR) from publication to resolution for a specific finding.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ id: z.string() }) }))
      .output(MTTRDataSchema),
    getAggregateMTTR: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/mttr/aggregate',
        summary: 'Get aggregate MTTR across findings',
        description:
          'Calculates aggregate Mean Time To Resolution (MTTR) across all findings within a date range. Returns average and median MTTR in days. Can be filtered by severity.',
        inputStructure: 'detailed',
      })
      .input(z.object({ query: AggregateMTTRQuerySchema }))
      .output(AggregateMTTRDataSchema),
    getOpenFindingsOverTime: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/analytics/open-over-time',
        summary: 'Get open findings count over time',
        description:
          'Retrieves the count of open (unresolved) findings over a specified time period. Can be filtered by severity and application.',
        inputStructure: 'detailed',
      })
      .input(z.object({ query: FindingsOverTimeQuerySchema }))
      .output(FindingsOverTimeResponseSchema),
    getFindingsByStatusOverTime: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/analytics/status-over-time',
        summary: 'Get resolved vs unresolved findings over time',
        description:
          'Retrieves the count of resolved and unresolved findings over a specified time period.',
        inputStructure: 'detailed',
      })
      .input(z.object({ query: FindingsByStatusOverTimeQuerySchema }))
      .output(FindingsByStatusOverTimeResponseSchema),
    getMTTROverTime: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/analytics/mttr-over-time',
        summary: 'Get MTTR trend over time by severity',
        description:
          'Returns Mean Time To Resolution (MTTR) in days for each date, broken down by severity level. Supports filtering by date range, environment, and business unit.',
        inputStructure: 'detailed',
      })
      .input(z.object({ query: DashboardTrendQuerySchema }))
      .output(MTTROverTimeResponseSchema),
    getResolvedUnresolvedTrend: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'GET',
        path: '/analytics/resolved-unresolved-trend',
        summary: 'Get resolved vs unresolved findings trend over time',
        description:
          'Returns the count of resolved and unresolved findings over time. Supports filtering by date range, environment, and business unit.',
        inputStructure: 'detailed',
      })
      .input(z.object({ query: DashboardTrendQuerySchema }))
      .output(ResolvedUnresolvedTrendResponseSchema),
    generatePocScript: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'POST',
        path: '/application/{applicationId}/findings/{findingId}/generate-poc-script',
        summary:
          'Trigger POC script generation for a finding (async; poll GET finding for pocScript)',
        inputStructure: 'detailed',
        successStatus: 202,
      })
      .input(
        z.object({
          params: GeneratePocScriptPathParamsSchema,
          body: z.object({}).optional(),
        }),
      )
      .output(GeneratePocScriptAcceptedSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: GeneratePocScriptErrorSchema },
        NOT_FOUND: { status: 404, data: GeneratePocScriptErrorSchema },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: GeneratePocScriptErrorSchema,
        },
      }),
    migratePocToBlocks: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_FINDINGS.name],
      })
      .route({
        method: 'POST',
        path: '/migrate-poc-to-blocks',
        summary:
          'Migrate poc text field to structured pocBlocks for all tenant findings',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: z.object({}).optional() }))
      .output(
        z.object({
          migratedCount: z.number(),
          skippedCount: z.number(),
        }),
      )
      .errors({
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
  });

export type FindingStatusDistribution = z.infer<
  typeof FindingStatusDistributionSchema
>;
